import { z } from "zod";
import { PERMISSION_KEY_PATTERN } from "@zion/sdk-contracts";
//#region src/routes.ts
/**
* The route-entry vocabulary every family file uses, and the `/v1/admin` prefix.
*
* A family file declares each route as a `RouteEntry` (`method`, `path`) beside its request and
* response schemas, and `route-census.ts` folds every family's entries into
* `ADMIN_CONTRACT_ROUTES`, which core-api's `admin-route-census.spec.ts` checks against
* `openapi.json`. A path with a parameter is written in OpenAPI form (`/v1/admin/users/{userId}`),
* which is what `openapi.json`'s `paths` keys use; the matching builder function
* (`adminUserPath(userId)`) fills it for the client.
*/
const ADMIN_API_PREFIX = "/v1/admin";
const httpMethodSchema = z.enum([
	"GET",
	"POST",
	"PUT",
	"PATCH",
	"DELETE"
]);
const routeEntrySchema = z.strictObject({
	method: httpMethodSchema,
	/**
	* OpenAPI-form path: `/v1/...` whose every segment is a literal in `[A-Za-z0-9_.~-]` or one
	* `{param}` placeholder named in letters only. The letters-only rule is what `fillPath` and the
	* app harness's `mswPath` match on, so a name outside it could ship a literal `{app_id}` (R-59).
	*/
	path: z.string().regex(/^\/v1(?:\/(?:[A-Za-z0-9_.~-]+|\{[A-Za-z]+\}))+$/)
});
/**
* Declare one route. Validates the entry at module load so a typo in a family file fails the
* package's own tests and never reaches a consumer.
*/
function route(method, path) {
	routeEntrySchema.parse({
		method,
		path
	});
	return Object.freeze({
		method,
		path
	});
}
/**
* A path parameter as the client sends it: one path segment, no slash, no empty string. UUIDs,
* slugs and app ids all fit; anything else is refused before a URL is built.
*
* The dot-segments `.` and `..` are refused too (zion-admin-app `uncovered-admin-routes` ruling
* R-008). Both match the segment grammar, and a URL resolver removes them: `/v1/admin/groups/..`
* resolves to `/v1/admin/`, so a builder filled with one would send the request to a different
* route. No real id is a dot-segment.
*/
const pathParamSchema = z.string().min(1).max(128).regex(/^[A-Za-z0-9._~-]+$/).refine((v) => v !== "." && v !== "..", { message: "a path parameter must not be the dot-segment . or .." });
/**
* An app permission key as a path segment (`notes:read`, `docs:write`): two lowercase segments
* joined by one colon, `PERMISSION_KEY_PATTERN`'s grammar. The colon is legal `pchar` and
* `pathParamSchema` refuses it, so a route that carries an app permission key in the path passes
* this schema for that parameter through `fillPath`'s third argument (R-27). A platform permission
* key (`platform:org-members:read`, three segments) has its own grammar,
* `PLATFORM_PERMISSION_KEY_PATTERN`, and the two apps-catalog routes that carry one pass a schema
* built from that instead.
*/
const permissionKeyPathParamSchema = z.string().min(1).max(128).regex(PERMISSION_KEY_PATTERN);
/**
* Fill every `{param}` placeholder with a validated, URI-encoded value.
*
* @param template the OpenAPI-form path
* @param params one value per placeholder; a missing one throws `route param missing: <name>`
* @param schemas an optional per-param schema; a param not named here is validated by
*   `pathParamSchema`. A schema here still yields one segment: the value is encoded with
*   `encodeURIComponent`, so a colon travels as `%3A` and a slash can never split a segment.
*/
function fillPath(template, params, schemas = {}) {
	return template.replace(/\{([A-Za-z]+)\}/g, (_match, name) => {
		const value = params[name];
		if (value === void 0) throw new Error(`route param missing: ${name}`);
		const schema = schemas[name] ?? pathParamSchema;
		return encodeURIComponent(schema.parse(value));
	});
}
//#endregion
export { ADMIN_API_PREFIX, fillPath, httpMethodSchema, pathParamSchema, permissionKeyPathParamSchema, route, routeEntrySchema };

//# sourceMappingURL=routes.js.map